import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getEmployeeProfile, saveEmployeeProfile } from '@/lib/employeeStorage';
import {
  ArrowLeft,
  Save,
  X,
  Upload,
  Camera,
  Plus,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Calendar,
  Loader2
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface EmployeeProfileData {
  title: string;
  phone: string;
  location: string;
  bio: string;
  education: string;
  specialization: string[];
  department: string;
  yearsExperience: number;
  startDate: string;
  imageUrl: string | null;
}

export default function EmployeeEditProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { allUsers } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [newSpecialization, setNewSpecialization] = useState('');
  const [formData, setFormData] = useState<EmployeeProfileData>({
    title: '',
    phone: '',
    location: '',
    bio: '',
    education: '',
    specialization: [],
    department: '',
    yearsExperience: 0,
    startDate: '',
    imageUrl: null
  });

  const employee = allUsers.find(u => u.id === id);

  // Load existing profile data asynchronously
  useEffect(() => {
    const loadProfile = async () => {
      if (!employee) return;
      
      setLoadingProfile(true);
      try {
        const data = await getEmployeeProfile(employee.email, employee.role);
        setFormData({
          title: data.title,
          phone: data.phone,
          location: data.location,
          bio: data.bio,
          education: data.education,
          specialization: data.specialization,
          department: data.department,
          yearsExperience: data.yearsExperience,
          startDate: data.startDate,
          imageUrl: data.imageUrl
        });
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };
    
    loadProfile();
  }, [employee]);

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Employee not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/users')}>
          Back to Directory
        </Button>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          imageUrl: reader.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const addSpecialization = () => {
    if (newSpecialization.trim() && !formData.specialization.includes(newSpecialization.trim())) {
      setFormData({
        ...formData,
        specialization: [...formData.specialization, newSpecialization.trim()]
      });
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (index: number) => {
    setFormData({
      ...formData,
      specialization: formData.specialization.filter((_, i) => i !== index)
    });
  };

  const handleSave = async () => {
    setLoading(true);
    
    await saveEmployeeProfile(employee.email, {
      title: formData.title,
      phone: formData.phone,
      location: formData.location,
      bio: formData.bio,
      education: formData.education,
      specialization: formData.specialization,
      department: formData.department,
      yearsExperience: formData.yearsExperience,
      startDate: formData.startDate,
      imageUrl: formData.imageUrl
    });

    setLoading(false);
    toast({
      title: "Profile updated",
      description: "Professional information has been updated successfully.",
    });
    navigate(`/employee/${id}`);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(`/employee/${id}`)} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Profile
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/employee/${id}`)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit Professional Information: {employee.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-gray-200 bg-gray-100">
                {formData.imageUrl ? (
                  <img 
                    src={formData.imageUrl}
                    alt={employee.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center bg-blue-100 text-blue-600 text-2xl font-bold">
                    {getInitials(employee.name)}
                  </div>
                )}
              </div>
              <label 
                htmlFor="profile-photo" 
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg"
              >
                <Camera className="h-4 w-4" />
              </label>
              <input
                type="file"
                id="profile-photo"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>
            <p className="text-xs text-muted-foreground">Click camera icon to update photo</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Job Title
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Senior Investment Analyst"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Start Date
              </Label>
              <Input
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                placeholder="January 2024"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone
              </Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1 (555) 000-0000"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Location
              </Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="City, State"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Bio</Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              placeholder="Brief description of the team member..."
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Education
            </Label>
            <Textarea
              value={formData.education}
              onChange={(e) => setFormData({...formData, education: e.target.value})}
              placeholder="e.g., MBA, Stanford University"
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Department</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData({...formData, department: e.target.value})}
                placeholder="e.g., Investment Analysis"
              />
            </div>
            <div className="space-y-2">
              <Label>Years Experience</Label>
              <Input
                type="number"
                value={formData.yearsExperience}
                onChange={(e) => setFormData({...formData, yearsExperience: parseInt(e.target.value) || 0})}
                placeholder="5"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Specializations</Label>
            <div className="flex gap-2">
              <Input
                value={newSpecialization}
                onChange={(e) => setNewSpecialization(e.target.value)}
                placeholder="Add a specialization"
                onKeyPress={(e) => e.key === 'Enter' && addSpecialization()}
              />
              <Button type="button" onClick={addSpecialization} variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.specialization.map((spec, index) => (
                <Badge key={index} variant="secondary" className="px-3 py-1">
                  {spec}
                  <button
                    onClick={() => removeSpecialization(index)}
                    className="ml-2 hover:text-red-500"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}